'use client'

import { useEffect, useState } from 'react'
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride'
import { useOnboarding } from '@/hooks/useOnboarding'
import { ClientTab } from '@/types/client'

interface OnboardingFlowProps {
  activeTab?: ClientTab
  onTabChange?: (tab: ClientTab) => void
  userRole?: 'client' | 'landlord' | 'admin'
}

// Note: activeTab and onTabChange are kept in the interface for API compatibility
// but are not currently used in the component implementation

/**
 * Onboarding Flow Component
 * Provides step-by-step guided tour for first-time users
 */
export default function OnboardingFlow({ userRole = 'client' }: OnboardingFlowProps) {
  const { shouldShowOnboarding, completeOnboarding } = useOnboarding(userRole)
  const [run, setRun] = useState(false)
  const [steps, setSteps] = useState<Step[]>([])

  // Define steps based on user role
  useEffect(() => {
    if (userRole === 'client') {
      setSteps([
        {
          target: '[data-onboarding="browse-properties"]',
          content: 'Browse verified properties here. Use filters to find exactly what you\'re looking for.',
          placement: 'bottom',
          title: 'Browse Properties',
        },
        {
          target: '[data-onboarding="property-card"]',
          content: 'Click on any property to view details. Save properties you love with the heart icon.',
          placement: 'top',
          title: 'Property Cards',
        },
        {
          target: '[data-onboarding="applications-tab"]',
          content: 'Track all your rental applications here. See status updates, chat with landlords, and make payments.',
          placement: 'right',
          title: 'My Applications',
        },
        {
          target: '[data-onboarding="saved-properties-tab"]',
          content: 'All your saved properties are stored here for easy access.',
          placement: 'right',
          title: 'Saved Properties',
        },
        {
          target: '[data-onboarding="profile-tab"]',
          content: 'Complete your KYC verification to unlock all features. Upload required documents here.',
          placement: 'right',
          title: 'Profile & KYC',
        },
      ])
    } else if (userRole === 'landlord') {
      setSteps([
        {
          target: '[data-onboarding="properties-list"]',
          content: 'Manage all your property listings here. Create new listings or edit existing ones.',
          placement: 'bottom',
          title: 'Your Properties',
        },
        {
          target: '[data-onboarding="applications-list"]',
          content: 'Review rental applications from potential tenants. Approve or reject applications here.',
          placement: 'bottom',
          title: 'Applications',
        },
        {
          target: '[data-onboarding="profile-tab"]',
          content: 'Complete your verification to start listing properties. Upload required documents here.',
          placement: 'right',
          title: 'Profile & Verification',
        },
      ])
    }
  }, [userRole])

  useEffect(() => {
    if (shouldShowOnboarding && steps.length > 0) {
      // Delay to ensure elements are rendered
      const timer = setTimeout(() => {
        setRun(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [shouldShowOnboarding, steps.length])

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data
    
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      completeOnboarding()
      setRun(false)
    }
  }

  if (!shouldShowOnboarding || steps.length === 0) {
    return null
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#10b981', // emerald-600
          textColor: '#1c1917', // stone-900
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          arrowColor: '#10b981',
        },
        buttonNext: {
          backgroundColor: '#10b981',
          color: '#fff',
          borderRadius: '8px',
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: '600',
        },
        buttonBack: {
          color: '#78716c', // stone-500
          marginRight: '8px',
          fontSize: '14px',
        },
        buttonSkip: {
          color: '#78716c',
          fontSize: '14px',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip tour',
      }}
    />
  )
}

